import styles from './styles.module.css'
import { useState, useContext, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { WoDetailContext } from '../../../../../work-orders/components/context/context'
import WorkOrderDetailModal from '../../../../../work-orders/components/work-order-detail/components/main/WorkOrderDetailModal'
import { ILocation } from '../../../../../../../app/entities/Location'
import determineOrderLateStatus from './utils/determine-order-late-status'
import { SERVICE_NAMES } from '../../../../constants/service'
import createTrackingLinks from '../../../../utils/tire-rack-order/create-tracking-links'
import TrackingLinksModal from './tracking-links/TrackingLinksModal'
import { CARRIERS_WITH_POSSIBLY_INVALID_INFO_ARRAY } from '../../../../constants/tire-rack-order'
import {
  cellPlacementStatuses,
  deliveryStatuses,
  installationStatuses,
} from '../../../../constants/technician-route-card-statuses'
import isDeliveryByOrderService from '../../../../utils/is-delivery-by-order-service'
import { getCellPlacementStatusOfOrder } from '../../../../utils/cell-tire-rack-order'
import { CELL_PLACEMENT_STATUS } from '../../../../constants/cell-tire-rack-order'
import {
  getAllTireRackOrderNumbers,
  getLatestDeliveredDate,
  getLatestShippingArrival,
  getTrackableTireRackOrders,
} from '../../../../utils/tire-rack-orders'
import { IOrder } from '../../../../../../../app/entities/Order'

interface TechCardProps {
  order: IOrder
  location: ILocation
  startTime: string
  endTime: string
}

const TRACK_IT_STR = 'track it'

export default function TechRouteCard(props: TechCardProps) {
  const {
    order,
    location: { timezoneOffset },
    startTime,
    endTime,
  } = props
  const { customer, orderServices, tireRackOrders, objectId, timeSlot } = order

  // Universal placeholders (e.g. REWORK) never represent a real shipment, so
  // they must not surface a shipping method or tracking links
  const trackableTireRackOrders = getTrackableTireRackOrders(tireRackOrders)
  const shippingMethod = trackableTireRackOrders
    .map((tro) => tro.shipping?.method)
    .filter((method) => method && method.length > 0)
    .join(', ')
  const shippingEta = getLatestShippingArrival(order)
  const deliveredOn = getLatestDeliveredDate(order)
  const shippingDateToDisplay = deliveredOn ? deliveredOn : shippingEta
  const shippingStatus = getShippingStatus(shippingEta, deliveredOn)
  const allTrackingLinks: string[] = []
  const allTrackingNumbers: string[] = []
  trackableTireRackOrders.forEach((tro) => {
    const { trackingLinks, trackingNumbers } = createTrackingLinks(tro)
    allTrackingLinks.push(...trackingLinks)
    allTrackingNumbers.push(...trackingNumbers)
  })
  const trackingInfo = {
    trackingNumbers: allTrackingNumbers,
    trackingLinks: allTrackingLinks,
  }
  const newTabTrackItId = `newTabTrackItId`
  const multiLinkTrackItId = `${objectId}-multiLinkTrackIt`

  const { workOrderId, status } = order
  const { firstName, lastName } = customer ?? {}
  const orderServiceQuantity =
    orderServices?.find(
      (orderService) => orderService.label !== SERVICE_NAMES.TIRE_DISPOSAL,
    )?.quantity ?? 0
  const orderNumbers = getAllTireRackOrderNumbers(order)
  const cells = tireRackOrders
    .map((tro) => tro?.cellPlacement?.cell?.name || '')
    .filter((str) => str.length > 0)

  const cellsDisplayText = cells.length > 0 ? cells.join(', ') : null

  const { fromDateGenerated } = timeSlot ?? {}
  const navigate = useNavigate()
  const { queryOrderData, clearData } = useContext(WoDetailContext)
  const [modalName, setModalName] = useState('')
  const [modalOpen, setModalOpen] = useState(false)

  function getShippingStatus(eta: any, deliveredOn: any) {
    if (deliveredOn) {
      return 'Delivered'
    } else if (eta) {
      return 'ETA'
    } else {
      return ''
    }
  }

  async function handleOpen(id: string) {
    const url = `/${id}`
    navigate(url)
    setModalName('Work Order Detail')
    setModalOpen(true)
    await queryOrderData(id)
  }
  function handleClosed() {
    setModalOpen(false)
    navigate('/')
    clearData()
  }

  const orderVisualStatus = determineOrderLateStatus(
    status,
    timezoneOffset,
    startTime,
    fromDateGenerated,
  )

  let borderStyle = ''
  if (orderVisualStatus === 'fixed') {
    borderStyle = '1px dotted #C2C2C2'
  } else if (
    orderVisualStatus === 'scheduled' ||
    orderVisualStatus === 'pending_rescheduling'
  ) {
    borderStyle = '1px solid #C2C2C2'
  }

  const isDelivery = isDeliveryByOrderService(order?.orderServices ?? [])
  const orderStatuses = isDelivery ? deliveryStatuses : installationStatuses
  const { display, backgroundColor, statusColor, textColor } =
    orderStatuses[orderVisualStatus ?? '']

  const cellPlacementStatusOfOrder = getCellPlacementStatusOfOrder(order)
  const cellStatuses =
    cellPlacementStatuses[
      cellPlacementStatusOfOrder ?? CELL_PLACEMENT_STATUS.ZERO_PLACED
    ]

  //order time display logic
  const orderTime = `${startTime} - ${endTime}`

  function generateTrackItLink(): JSX.Element {
    if (!allTrackingLinks || allTrackingLinks.length <= 0) {
      return <div />
    } else if (allTrackingLinks.length === 1) {
      return (
        <a
          href={allTrackingLinks[0]}
          target='_blank'
          rel='noreferrer'
          id={newTabTrackItId}
        >
          {TRACK_IT_STR}
        </a>
      )
    } else {
      // if there are multiple tracking links, we know that we need to
      // open a modal to display them
      return (
        <a
          href='#'
          id={multiLinkTrackItId}
        >
          {TRACK_IT_STR}
        </a>
      )
    }
  }

  // prevent work orders detail modal from opening when clicking on tracking links
  useEffect(() => {
    const ontracTrackingLink = document.getElementById(multiLinkTrackItId)
    const newTabTrackingLink = document.getElementById(newTabTrackItId)
    if (ontracTrackingLink) {
      ontracTrackingLink.addEventListener('click', function (e) {
        setModalOpen(true)
        setModalName('Tracking Links')

        e.stopPropagation()
      })
    }
    if (newTabTrackingLink) {
      newTabTrackingLink.addEventListener('click', function (e) {
        e.stopPropagation()
      })
    }
  }, [allTrackingLinks])

  function whichModalContent(): JSX.Element {
    if (modalName === 'Work Order Detail') {
      return (
        <WorkOrderDetailModal
          handleClosed={handleClosed}
          isOpen={modalOpen}
        />
      )
    } else if (modalName === 'Tracking Links') {
      return (
        <TrackingLinksModal
          trackingInfo={trackingInfo}
          isOpen={modalOpen}
          handleClosed={handleClosed}
        />
      )
    } else {
      return <div />
    }
  }

  return (
    <>
      {whichModalContent()}
      <div
        onClick={() => handleOpen(objectId)}
        className={styles.cardContainer}
        style={{ backgroundColor: backgroundColor, border: borderStyle }}
      >
        <div className={styles.cardTopContainer}>
          <div>
            <section className={styles.section}>
              <div className={styles.orderNumbers}>
                {workOrderId}[{orderServiceQuantity}]
              </div>
              <div className={styles.orderTime}>{orderTime}</div>
            </section>
            <section>
              <div className={styles.custName}>
                {firstName} {lastName}
              </div>
            </section>
          </div>
          <section className={styles.section}>
            <div
              className={styles.statusOfOrder}
              style={{ backgroundColor: statusColor }}
            >
              <span
                className={styles.statusText}
                style={{ color: textColor }}
              >
                {display}
              </span>
            </div>
          </section>
        </div>
        <div className={styles.cardBottomContainer}>
          <div>
            <section className={styles.section}>
              <div className={styles.shippingMethod}>
                {orderNumbers.join(', ')}
              </div>
            </section>
            <section className={styles.section}>
              <div className={styles.shippingMethod}>{shippingMethod}</div>
            </section>
            {!CARRIERS_WITH_POSSIBLY_INVALID_INFO_ARRAY.includes(
              shippingMethod,
            ) ? (
              <section className={styles.section}>
                <div className={styles.shippingInfo}>
                  <div className={styles.shippingStatus}>{shippingStatus}</div>
                  <div>
                    {shippingDateToDisplay
                      ? shippingDateToDisplay.toLocaleDateString()
                      : ''}
                  </div>
                  <div>{generateTrackItLink()}</div>
                </div>
              </section>
            ) : (
              <div />
            )}
          </div>
          <div style={{ alignContent: 'flex-end' }}>
            {cellsDisplayText ? (
              <section className={styles.section}>
                <div
                  className={styles.statusOfOrder}
                  style={{
                    backgroundColor: cellStatuses.statusColor,
                  }}
                >
                  <span
                    className={styles.statusText}
                    style={{ color: cellStatuses.textColor }}
                  >
                    {cellsDisplayText}
                  </span>
                </div>
              </section>
            ) : (
              <div />
            )}
          </div>
        </div>
      </div>
    </>
  )
}
